/**
 * What the bubble and the hover peek say about a device (build decisions §3,
 * the "bubble anatomy" board). The words are the Fleet and Devices tables',
 * from the same functions, so the bubble can never call a robot something its
 * row does not.
 */
import { useClosedLiftScopes } from '../../../api/hooks/useLiftRules';
import { useContext, type ReactNode } from 'react';
import { useRobots, useTasks } from '../../../api/hooks';
import {
  commissionKey,
  useRobotCommissionStates,
} from '../../../api/hooks/useCommissionRobot';
import { useDeviceNotes } from '../../../api/hooks/useDeviceNotes';
import { useFeedHealth } from '../../../api/feed-health';
import { useDoorHealth, useDoors } from '../../../api/hooks/useDoors';
import { useLifts, useLiftWriteGate } from '../../../api/hooks/useLifts';
import { usePlaces } from '../../../api/hooks/usePlaces';
import { robotReceiptKey, useRobotReceipts } from '../../../api/robot-receipts';
import { AuthContext } from '../../../auth/AuthContext';
import { audienceFor } from '../../../auth/roles';
import { LIFT_ARM_WRITES_ROLES } from '../../../auth/types';
import { StatusChip } from '../../../design-system/components/StatusChip';
import { BATTERY_LOW_PCT } from '../../../state/battery-levels';
import { useMinuteTick, useNowTick } from '../../../state/clock';
import { useActiveSite } from '../../../state/useActiveSite';
import { DoorOverrideChip } from '../../Devices/DoorOverridePanel';
import {
  doorControllerCell,
  doorLastChange,
  doorStateCell,
  liftModeCell,
  liftRobotCell,
} from '../../Devices/devices-rows';
import { formatClock, type LiftRobotFacts } from '../../Devices/lift-reading';
import { useLiftColumnState } from '../../Devices/useLiftColumnState';
import { useSiteLiftCars } from '../../Devices/useSiteLiftCars';
import {
  doingWords,
  outOfServiceWords,
  robotStatusCell,
  robotTaskOf,
  whereWords,
} from '../../Fleet/fleet-rows';
import { batteryText, heardText, isReportingNow } from '../../Fleet/robot-lines';
import type { MapDevice } from '../../Map/map-bubble-anchor';
import type { RobotPresence } from '../../Map/robot-presence';
import { resolveSiteTimeZone } from '../../Tasks/site-timezone';
import { useDoorOverrides } from '../../../state/useDoorOverrides';
import { remainingS } from '../../../state/door-overrides';
import { BubbleTaskAction } from './BubbleTaskAction';

export type RobotPresenceMap = ReadonlyMap<string, RobotPresence>;

function useSiteClock() {
  const { site, sites } = useActiveSite();
  const zone = resolveSiteTimeZone(sites, site?.id);
  const siteZone = zone.source === 'site' ? zone.timeZone : null;
  const wallNowMs = useMinuteTick();
  return {
    siteId: site?.id ?? '',
    siteZone,
    wallNowMs,
    clock: (ms: number) => formatClock(ms, siteZone, wallNowMs),
  };
}

// ----------------------------------------------------------------------------
// Robot.
// ----------------------------------------------------------------------------

function useRobotFacts(name: string, presenceMap: RobotPresenceMap) {
  const audience = audienceFor(useContext(AuthContext)?.roles);
  const { siteId, siteZone, wallNowMs, clock } = useSiteClock();
  const robot = (useRobots().data ?? []).find((r) => r.name === name);
  const tasks = useTasks().data;
  const commission = useRobotCommissionStates();
  const feed = useFeedHealth(siteId);
  const places = usePlaces().data ?? [];
  const notes = useDeviceNotes().data;
  const info = robot ? commission.data?.get(commissionKey(robot.fleet, robot.name)) : undefined;
  const presence = robot ? presenceMap.get(robotReceiptKey(robot.fleet, robot.name)) : undefined;
  const task = robot ? robotTaskOf(robot, tasks ?? []) : null;
  const status = robot
    ? robotStatusCell({
        robot,
        presence,
        clamped: feed.clamped,
        commission: info?.state ?? 'unknown',
        issues: info?.issues ?? [],
        task,
        audience,
        outOfServiceWords: outOfServiceWords(
          notes?.robots_out.find((n) => n.fleet === robot.fleet && n.robot === robot.name),
          clock,
        ),
      })
    : null;
  return { robot, presence, task, status, feed, places, siteId, siteZone, wallNowMs, clock };
}

function batteryColor(battery: number): string {
  if (battery < BATTERY_LOW_PCT) return 'var(--nx-error)';
  if (battery < 50) return 'var(--nx-warning)';
  return 'var(--nx-cyan)';
}

export function RobotBubbleBody({
  name,
  presenceMap,
  lifts,
  footer,
}: {
  name: string;
  presenceMap: RobotPresenceMap;
  lifts: Parameters<typeof BubbleTaskAction>[0]['lifts'];
  footer: ReactNode;
}) {
  const liftClosed = useClosedLiftScopes();
  const { robot, presence, task, status, feed, places, siteId, siteZone, wallNowMs, clock } =
    useRobotFacts(name, presenceMap);
  useNowTick(1_000);
  const { book } = useRobotReceipts(siteId);
  if (!robot || !status) return <BubbleHead name={name} chip={null} />;
  const battery = batteryText(robot.battery, presence, feed.clamped);
  const receipt = book.get(robotReceiptKey(robot.fleet, robot.name));
  const heard = heardText(presence, {
    agoMs: receipt.lastAt === null ? null : book.now() - receipt.lastAt,
    count: receipt.count,
  });
  const where = whereWords(presence, places, siteZone, wallNowMs);
  const showAction = task !== null && status.cls !== 'out_of_service';
  return (
    <>
      <BubbleHead name={robot.name} chip={<StatusChip status={status.status} label={status.label} />} />
      {status.small && status.cls !== 'working' && (
        <p className="nx-map-bubble__small">{status.small}</p>
      )}
      {battery !== '—' && isReportingNow(presence, feed.clamped) && (
        <div className="nx-map-bubble__battery">
          <span className="nx-map-bubble__battery-bar" aria-hidden="true">
            <span
              className="nx-map-bubble__battery-fill"
              style={{
                width: `${Math.max(0, Math.min(100, robot.battery ?? 0))}%`,
                background: batteryColor(robot.battery ?? 0),
              }}
            />
          </span>
          <span>{battery}</span>
        </div>
      )}
      <p className="nx-map-bubble__line" data-testid="bubble-doing">
        {doingWords({ task, nextRun: null, clock, liftClosed })}
      </p>
      <p className="nx-map-bubble__small" data-testid="bubble-where">
        {[where, heard].filter(Boolean).join(' · ')}
      </p>
      <div className="nx-map-bubble__buttons">
        {footer}
        {showAction && <BubbleTaskAction task={task} robot={robot} lifts={lifts} />}
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// Lift.
// ----------------------------------------------------------------------------

function useLiftFacts(name: string) {
  const { wallNowMs, clock } = useSiteClock();
  const lifts = useLifts().data;
  const lift = (lifts ?? []).find((l) => l.name === name);
  const gate = useLiftWriteGate().data;
  const facts = useSiteLiftCars();
  const cars: LiftRobotFacts[] = (facts ?? []).some((c) => c.name === name)
    ? [...(facts ?? [])]
    : [...(facts ?? []), { name, use: 'between_floors' }];
  const columns = useLiftColumnState({ cars, lifts, gate });
  const column = columns.get(name);
  return { lift, column, wallNowMs, clock };
}

export function LiftBubbleBody({
  name,
  onRecover,
  footer,
}: {
  name: string;
  onRecover: () => void;
  footer: ReactNode;
}) {
  const auth = useContext(AuthContext);
  const { lift, column, clock } = useLiftFacts(name);
  const nowMs = useNowTick(5_000);
  if (!lift) return <BubbleHead name={name} chip={null} />;
  const mode = liftModeCell(lift, column, clock);
  const robot = liftRobotCell(lift, column, nowMs);
  const level = column?.level ?? null;
  const door = column?.door ?? null;
  const carLine =
    level === null
      ? null
      : `Car at ${level}${door === null ? '' : `, doors ${door === 'moving' ? 'moving' : door}`}`;
  const canRecover = auth?.roles?.some((role) => LIFT_ARM_WRITES_ROLES.includes(role)) === true;
  return (
    <>
      <BubbleHead
        name={lift.name}
        chip={<StatusChip status={mode.status} label={mode.label} dashed={mode.dashed} />}
      />
      {mode.small && <p className="nx-map-bubble__small">{mode.small}</p>}
      {carLine && <p className="nx-map-bubble__line">{carLine}</p>}
      <p
        className={`nx-map-bubble__line${robot.stuck ? ' nx-map-bubble__line--stuck' : ''}`}
        data-testid="bubble-lift-robot"
      >
        {robot.text}
      </p>
      <div className="nx-map-bubble__buttons">
        {footer}
        {/* Opens the panel, as on Devices: the press and its checks are there. */}
        {robot.stuck && canRecover && (
          <button
            type="button"
            className="nx-screen-btn nx-map-bubble__danger"
            onClick={onRecover}
            data-testid="bubble-recover"
          >
            Recover…
          </button>
        )}
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// Door.
// ----------------------------------------------------------------------------

export function DoorBubbleBody({ name, footer }: { name: string; footer: ReactNode }) {
  const audience = audienceFor(useContext(AuthContext)?.roles);
  const door = (useDoors().data ?? []).find((d) => d.name === name);
  const health = useDoorHealth().byDoor?.get(name);
  const overrides = useDoorOverrides();
  const nowMs = useNowTick(5_000);
  if (!door) return <BubbleHead name={name} chip={null} />;
  const state = doorStateCell(door);
  const change = doorLastChange(door, audience, nowMs);
  const controller = doorControllerCell(health);
  const override = overrides.byDoor.get(name);
  return (
    <>
      <BubbleHead
        name={door.name}
        chip={<StatusChip status={state.status} label={state.label} dashed={state.dashed} />}
      />
      {override && <DoorOverrideChip remaining={remainingS(override, overrides.now)} />}
      {change !== '—' && <p className="nx-map-bubble__line">{change}</p>}
      {controller && (
        <p className="nx-map-bubble__small">Controller: {controller.label.toLowerCase()}</p>
      )}
      <div className="nx-map-bubble__buttons">{footer}</div>
    </>
  );
}

function BubbleHead({ name, chip }: { name: string; chip: ReactNode }) {
  return (
    <div className="nx-map-bubble__head">
      <strong className="nx-map-bubble__name">{name}</strong>
      {chip}
    </div>
  );
}

// ----------------------------------------------------------------------------
// The hover peek: one line.
// ----------------------------------------------------------------------------

function RobotPeek({ name, presenceMap }: { name: string; presenceMap: RobotPresenceMap }) {
  const { robot, presence, status, feed } = useRobotFacts(name, presenceMap);
  if (!robot || !status) return <>{name}</>;
  const battery = batteryText(robot.battery, presence, feed.clamped);
  return <>{[robot.name, status.label, battery === '—' ? null : battery].filter(Boolean).join(' · ')}</>;
}

function LiftPeek({ name }: { name: string }) {
  const { lift, column, clock } = useLiftFacts(name);
  if (!lift) return <>{name}</>;
  return <>{`${lift.name} · ${liftModeCell(lift, column, clock).label}`}</>;
}

function DoorPeek({ name }: { name: string }) {
  const door = (useDoors().data ?? []).find((d) => d.name === name);
  return <>{door ? `${door.name} · ${doorStateCell(door).label}` : name}</>;
}

export function PeekWords({
  device,
  presenceMap,
}: {
  device: MapDevice;
  presenceMap: RobotPresenceMap;
}) {
  if (device.kind === 'robot') return <RobotPeek name={device.id} presenceMap={presenceMap} />;
  if (device.kind === 'lift') return <LiftPeek name={device.id} />;
  return <DoorPeek name={device.id} />;
}
